import { captureException } from '@sentry/browser';
import { loader } from 'graphql.macro';
import { get, isEmpty, isNull, isNumber, last, sortBy } from 'lodash';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { Http } from 'src/shared/services/Http';

import { Assessment, AssessmentType, MappedAnswers } from '../models/assessments';

const GET_ASSESSMENTS_QUERY = loader('../documents/getAssessmentsQuery.graphql');
const GET_CONTENT_RESPONSES_QUERY = loader('../documents/getContentResponsesQuery.graphql');
const SUBMIT_ASSESSMENTS_MUTATION = loader('../documents/submitAssessmentsMutation.graphql');
const SUBMIT_MODULARIZED_ASSESSMENTS_MUTATION = loader(
    '../documents/submitModularizedAssessmentsMutation.graphql',
);

interface Who5Survey {
    createdAt: string;
    who5_score: number;
}

// cache go away please
export class AssessmentsService {
    constructor(private http: Http) {
        this.http = http;
    }

    // used to retrieve phq2 & gad2 question from phq8 & gad7 assessments
    // replaces a number in a key e.g. `phq8_littleinterest` will become `phq2_littleinterest`
    private getFirstTwoAnswers = (
        dataToSubmit: Record<AssessmentType, MappedAnswers>,
        sourceAssessment: AssessmentType,
        targetAssessment: AssessmentType,
    ) => {
        const [firstAnswer, secondAnswer] = Object.entries(dataToSubmit[sourceAssessment]);

        return {
            [firstAnswer[0].replace(sourceAssessment, targetAssessment)]: firstAnswer[1],
            [secondAnswer[0].replace(sourceAssessment, targetAssessment)]: secondAnswer[1],
        };
    };

    // Backend expects snake_case so this converts phq8remainder to phq8_remainder
    getAssessmentSubmissionKey = (assessmentKey: string): string => {
        if (assessmentKey.includes('remainder') && !assessmentKey.includes('_remainder')) {
            return assessmentKey.replace('remainder', '_remainder');
        }
        return assessmentKey;
    };

    getAssessmentKeyFromAnswerKey = answerItem => {
        const questionKey = answerItem[0];
        let assessmentKey;
        if (questionKey.includes(AssessmentType.EmploymentHealth)) {
            assessmentKey = AssessmentType.EmploymentHealth;
        } else {
            assessmentKey = questionKey.split('_')[0];
        }
        return this.getAssessmentSubmissionKey(assessmentKey);
    };

    public async submitModularizedAssessments(
        answers: MappedAnswers,
        assessment: {
            name: string;
            assessmentFlowUuid: string;
        },
        careRecommendationUuid?: string,
    ): Promise<{ scores: Record<string, number>; nextAssessment: string }> {
        const answersArray = Object.entries(answers);
        const assessmentKey = this.getAssessmentSubmissionKey(assessment.name);
        const allAnswersMatchAssessment = answersArray.every(answerItem => {
            return this.getAssessmentKeyFromAnswerKey(answerItem) === assessmentKey;
        });

        if (!allAnswersMatchAssessment) {
            captureException({
                message: 'Received question/answer pairs that do not match the current assessment',
            });
            // If this Sentry log is hit, you may:
            // 1. Be getting the wrong question/answer pairs
            // 2. Be getting the wrong assessment.name
            // 3. Need to reintroduce logic to support submitting responses for multiple assessments:
            // https://gitlab.com/modernhealth/member-webapp-frontend/-/blob/f1efccd1c14819daedf41f402416af93ec291fe2/frontend/src/modules/assessments/services/AssessmentsService.ts#L130
        }

        const answersObject = answersArray.reduce((acc, [key, value]) => {
            return {
                ...acc,
                [key]: value,
            };
        }, {});
        const dataToSubmit = {
            [assessmentKey]: answersObject,
        };

        try {
            const { data } = await appClient.mutate({
                mutation: SUBMIT_MODULARIZED_ASSESSMENTS_MUTATION,
                variables: {
                    input: {
                        careRecommendationUuid,
                        content: JSON.stringify(dataToSubmit),
                        assessmentFlowUuid: assessment.assessmentFlowUuid,
                    },
                },
            });

            const scores = {};
            const nextAssessment = data?.submitModularizedAssessments?.nextAssessments?.[0] || '';

            try {
                data?.submitModularizedAssessments?.createdContentResponses?.forEach(
                    ({ delta }) => {
                        Object.entries(JSON.parse(delta)).forEach(([key, value]) => {
                            scores[key] = (value as { score: number })['score'];
                        });
                    },
                );
            } catch (error) {
                captureException(error);
            }

            return {
                nextAssessment,
                scores,
            };
        } catch (error) {
            return Promise.reject(config.messages.genericError);
        }
    }

    public async submitAssessments(
        answers: MappedAnswers,
        assessmentFlowUuid: string,
    ): Promise<Record<string, number>> {
        const possibleAssessments = Object.keys(AssessmentType).map(name => AssessmentType[name]);
        const answersArray = Object.entries(answers);

        const dataToSubmit = possibleAssessments.reduce((acc, current) => {
            const currentAssessmentAnswers = {};

            answersArray
                .filter(([key]) => key.includes(current))
                .forEach(([key, value]) => {
                    if (value !== '' && !isNull(value)) {
                        currentAssessmentAnswers[key] = value;
                    }
                });

            return isEmpty(currentAssessmentAnswers)
                ? acc
                : {
                      ...acc,
                      [current]: currentAssessmentAnswers,
                  };
        }, {});

        // if user filled phq8 assessment we also want to submit phq2
        if (dataToSubmit?.[AssessmentType.Phq8]) {
            dataToSubmit[AssessmentType.Phq2] = this.getFirstTwoAnswers(
                dataToSubmit,
                AssessmentType.Phq8,
                AssessmentType.Phq2,
            );
        }

        // if user filled gad7 assessment we also want to submit gad2
        if (dataToSubmit?.[AssessmentType.Gad7]) {
            dataToSubmit[AssessmentType.Gad2] = this.getFirstTwoAnswers(
                dataToSubmit,
                AssessmentType.Gad7,
                AssessmentType.Gad2,
            );
        }

        try {
            const { data } = await appClient.mutate({
                mutation: SUBMIT_ASSESSMENTS_MUTATION,
                variables: {
                    content: JSON.stringify(dataToSubmit),
                    assessmentFlowUuid,
                },
            });

            const scores = {};

            try {
                data?.submitAssessments?.createdContentResponses?.forEach(({ delta }) => {
                    Object.entries(JSON.parse(delta)).forEach(([key, value]) => {
                        scores[key] = (value as { score: number })['score'];
                    });
                });
            } catch (error) {
                captureException(error);
            }

            return scores;
        } catch (error) {
            return Promise.reject(config.messages.genericError);
        }
    }

    public async fetchScores(): Promise<{
        date: string;
        scores: Record<string, number>;
    }> {
        try {
            const who5Surveys = await this.http.GET<Who5Survey[]>('survey/who5/');

            const mostRecentWho5Survey = last(
                sortBy(who5Surveys, ({ createdAt }) => new Date(createdAt)),
            );

            const { data } = await appClient.query({
                query: GET_CONTENT_RESPONSES_QUERY,
                fetchPolicy: 'network-only',
            });

            const who5Score = get(mostRecentWho5Survey, 'who5_score');

            const scores = {
                who5: isNumber(who5Score) ? who5Score : -1,
            };

            const content = get(data, 'contentResponses.content');

            if (content) {
                const parsedContent = JSON.parse(content);

                Object.keys(parsedContent).forEach(key => {
                    scores[key] = parsedContent[key].score;
                });
            }

            return {
                date: mostRecentWho5Survey?.createdAt || '',
                scores,
            };
        } catch (error) {
            captureException(error);

            return Promise.reject(config.messages.genericError);
        }
    }

    public async fetchAssessments(): Promise<Assessment[]> {
        try {
            const { data } = await appClient.query({
                query: GET_ASSESSMENTS_QUERY,
                fetchPolicy: 'network-only',
            });

            return data?.assessments.map(assessment => ({
                ...assessment,
                name: assessment.name.toLowerCase(),
            }));
        } catch (error) {
            return Promise.reject(config.messages.genericError);
        }
    }
}
